import { forwardRef, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export const inputClass =
  'h-9 w-full rounded-xl border border-input bg-card px-2.5 text-[13px] text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/70 focus-visible:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/20';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Icon rendered inside the field on the left. */
  leading?: ReactNode;
  /** Element rendered inside the field on the right (e.g. clear button, shortcut hint). */
  trailing?: ReactNode;
  inputSize?: 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, leading, trailing, inputSize = 'md', ...props }, ref) => {
    const sizeCls = inputSize === 'lg' ? 'h-11 text-sm' : '';
    if (!leading && !trailing) {
      return <input ref={ref} className={cn(inputClass, sizeCls, className)} {...props} />;
    }
    return (
      <div className={cn('relative flex items-center', className)}>
        {leading && (
          <span className="pointer-events-none absolute left-2.5 flex text-muted-foreground [&_svg]:size-4">{leading}</span>
        )}
        <input
          ref={ref}
          className={cn(inputClass, sizeCls, leading && 'pl-8', trailing && 'pr-8')}
          {...props}
        />
        {trailing && <span className="absolute right-1.5 flex items-center">{trailing}</span>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('text-xs font-medium text-foreground', className)} {...props} />;
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-xs text-danger">
      {children}
    </p>
  );
}
